import { createFileRoute } from '@tanstack/react-router';
import { AdminInvitationsView } from '@/components/admin/AdminInvitationsView';
import { AdminGroupInvitesView } from '@/components/admin/AdminGroupInvitesView';
import type {
  GroupInvitesPageParams,
  InvitationsPageParams,
} from '@/lib/queries/admin';

type Status = 'pending' | 'accepted' | 'revoked' | 'expired';
type Source = 'platform' | 'groups';

interface InvitationsSearch {
  readonly status?: Status;
  readonly source?: Source;
  readonly cursor?: string;
}

const ALLOWED_STATUS: ReadonlyArray<Status> = [
  'pending',
  'accepted',
  'revoked',
  'expired',
];

export const Route = createFileRoute('/_authed/admin/invitations/')({
  validateSearch: (raw: Record<string, unknown>): InvitationsSearch => {
    const status =
      typeof raw.status === 'string' &&
      (ALLOWED_STATUS as ReadonlyArray<string>).includes(raw.status)
        ? (raw.status as Status)
        : undefined;
    const source = raw.source === 'groups' ? ('groups' as const) : undefined;
    const cursor =
      typeof raw.cursor === 'string' && raw.cursor.length > 0
        ? raw.cursor
        : undefined;
    return {
      ...(status !== undefined ? { status } : {}),
      ...(source !== undefined ? { source } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    };
  },
  component: AdminInvitationsPage,
});

function AdminInvitationsPage() {
  const search = Route.useSearch();
  const filter = search.status ?? 'pending';

  if (search.source === 'groups') {
    const listParams: GroupInvitesPageParams = {
      status: filter,
      pageSize: 50,
      ...(search.cursor ? { cursor: search.cursor } : {}),
    };
    return (
      <AdminGroupInvitesView
        listParams={listParams}
        filter={filter}
        cursor={search.cursor}
      />
    );
  }

  const listParams: InvitationsPageParams = {
    status: filter,
    pageSize: 50,
    ...(search.cursor ? { cursor: search.cursor } : {}),
  };

  return (
    <AdminInvitationsView
      listParams={listParams}
      filter={filter}
      cursor={search.cursor}
    />
  );
}
